/**
 * checklist.js — the floating anticipation checklist component, extracted verbatim from preview.js.
 *
 * Self-contained: the functions here depend only on the DOM (`document`), the SVG namespace, and
 * the `cfg` object passed in by the caller. They hold NO closure over the preview instance state —
 * positioning is handled by the caller via the returned controller's `el`. Only `makeChecklist` is
 * part of the module surface; the three make* helpers are internal to it.
 */

const SVG_NS = "http://www.w3.org/2000/svg";

// ===========================================================================
// ANTICIPATION CHECKLIST (the floating stage tracker)
// ===========================================================================
// A small card that floats at the inset's top-left (bleeding slightly onto the
// frame border so it reads as HOVERING above the product). It replaces the old
// single-line status pill with the now-standard "doing this → now this → done"
// checklist. The Cruise Maps stamp rides the ACTIVE stage row and slides down as
// each stage completes, leaving a tick (and a struck-through label) behind — a
// nice touch that says "it's us, and here's where we are". The four stages map
// 1:1 onto the choreography beats:
//   0 Fetching your data  — the 2s pre-roll, before anything maps-related shows
//   1 Building your route — the route trace
//   2 Building your map   — the coastline skeleton fade
//   3 Map ready           — trace finished; held a beat, then the morph

// the completed-stage tick, centred in the icon column.
function makeChecklistTick(cfg, iconCol, rowH) {
  const svg = document.createElementNS(SVG_NS, "svg");
  svg.setAttribute("viewBox", "0 0 24 24");
  const size = 13;
  Object.assign(svg.style, {
    position: "absolute",
    left: `${(iconCol - size) / 2}px`,
    top: `${(rowH - size) / 2}px`,
    width: `${size}px`,
    height: `${size}px`,
    opacity: "0",
    transform: "scale(0.5)",
    transformOrigin: "center",
    transition: "opacity 220ms ease, transform 260ms cubic-bezier(.34,1.4,.5,1)",
    overflow: "visible",
  });
  const path = document.createElementNS(SVG_NS, "path");
  path.setAttribute("d", "M5 12.5 L10 17.5 L19.5 6.5");
  path.setAttribute("fill", "none");
  path.setAttribute("stroke", cfg.checklistTickColor);
  path.setAttribute("stroke-width", "3");
  path.setAttribute("stroke-linecap", "round");
  path.setAttribute("stroke-linejoin", "round");
  svg.appendChild(path);
  return svg;
}

// the brand stamp (or a placeholder roundel) that rides the active row.
function makeChecklistLogoInner(cfg) {
  if (cfg.checklistLogoUrl) {
    const img = document.createElement("img");
    img.src = cfg.checklistLogoUrl;
    img.alt = "";
    Object.assign(img.style, {
      width: "100%", height: "100%", objectFit: "contain", display: "block",
    });
    // graceful fallback if the URL ever fails to load.
    img.onerror = () => {
      const ph = makeChecklistPlaceholderMark(cfg);
      img.replaceWith(ph);
    };
    return img;
  }
  return makeChecklistPlaceholderMark(cfg);
}

// inline placeholder mark — a compass-star roundel in brand colours, used only
// when no checklistLogoUrl is supplied.
function makeChecklistPlaceholderMark(cfg) {
  const svg = document.createElementNS(SVG_NS, "svg");
  svg.setAttribute("viewBox", "0 0 24 24");
  Object.assign(svg.style, { width: "100%", height: "100%", display: "block" });
  const ring = document.createElementNS(SVG_NS, "circle");
  ring.setAttribute("cx", "12"); ring.setAttribute("cy", "12"); ring.setAttribute("r", "10.5");
  ring.setAttribute("fill", "none");
  ring.setAttribute("stroke", cfg.routeColor || "#203C51");
  ring.setAttribute("stroke-width", "1.5");
  const star = document.createElementNS(SVG_NS, "path");
  star.setAttribute("d", "M12 3 L13.4 10.6 L21 12 L13.4 13.4 L12 21 L10.6 13.4 L3 12 L10.6 10.6 Z");
  star.setAttribute("fill", cfg.accentColor || "#B7835A");
  svg.append(ring, star);
  return svg;
}

/**
 * Build the floating checklist. Returns a small controller:
 *   { el, advanceTo(i), complete(), fadeIn(), fadeOut(), destroy() }
 * advanceTo(i) marks rows < i done (tick + strikethrough), row i active (logo +
 * emphasis), rows > i pending; the logo SLIDES to row i. complete() ticks every
 * row and retires the logo. Positioning is handled by the caller (it knows the
 * inset geometry) via `el`.
 */
export function makeChecklist(cfg) {
  const stages = [cfg.copyStageFetch, cfg.copyStageRoute, cfg.copyStageMap, cfg.copyStageReady];
  const rowH = cfg.checklistRowHeight;
  const padY = 9, padX = 12, iconCol = 22, gap = 8, logoSize = 16, dotSize = 8;

  const card = document.createElement("div");
  card.className = "vp-checklist";
  Object.assign(card.style, {
    position: "absolute",
    zIndex: "20",
    boxSizing: "border-box",
    padding: `${padY}px ${padX}px`,
    background: cfg.checklistCardBg,
    borderRadius: "12px",
    boxShadow: cfg.checklistShadow,
    backdropFilter: "blur(2px)",
    WebkitBackdropFilter: "blur(2px)",
    font: "500 12.5px/1 system-ui, -apple-system, 'Segoe UI', sans-serif",
    color: cfg.checklistTextColor,
    pointerEvents: "none",
    opacity: "0",
    transform: "translateY(-5px)",
    transition: "opacity 320ms ease, transform 320ms ease",
    userSelect: "none",
    WebkitUserSelect: "none",
  });

  // The logo lives in a WRAP that owns the vertical travel (transform/translateY).
  // The stamp itself stays STILL (no spin); a soft blue GLOW disc behind it pulses
  // (fade + gentle breathe) to signal "working". Glow and travel are on separate
  // elements so their transforms never clobber each other.
  const logoWrap = document.createElement("div");
  Object.assign(logoWrap.style, {
    position: "absolute",
    left: `${padX + (iconCol - logoSize) / 2}px`,
    top: `${padY + (rowH - logoSize) / 2}px`,
    width: `${logoSize}px`,
    height: `${logoSize}px`,
    transform: "translateY(0px)",
    transition: "transform 520ms cubic-bezier(.34,1.18,.5,1), opacity 300ms ease",
    opacity: "0",
    zIndex: "2", // sit above the row dots/text
    willChange: "transform",
  });
  // pulsing blue glow behind the stamp (a halo larger than the logo).
  const glowSize = logoSize * 2.2;
  const glow = document.createElement("div");
  Object.assign(glow.style, {
    position: "absolute",
    left: `${(logoSize - glowSize) / 2}px`,
    top: `${(logoSize - glowSize) / 2}px`,
    width: `${glowSize}px`,
    height: `${glowSize}px`,
    borderRadius: "50%",
    background: `radial-gradient(circle, ${cfg.checklistPulseColor} 0%, transparent 68%)`,
    pointerEvents: "none",
    opacity: "0.45",
    willChange: "opacity, transform",
  });
  const logoInner = document.createElement("div");
  Object.assign(logoInner.style, { position: "relative", width: "100%", height: "100%" });
  logoInner.appendChild(makeChecklistLogoInner(cfg));
  logoWrap.append(glow, logoInner);
  card.appendChild(logoWrap);

  const pulse = glow.animate(
    [
      { opacity: 0.12, transform: "scale(0.78)" },
      { opacity: 0.7, transform: "scale(1.15)" },
      { opacity: 0.12, transform: "scale(0.78)" },
    ],
    { duration: 1700, iterations: Infinity, easing: "ease-in-out" }
  );

  const rows = stages.map((label) => {
    const row = document.createElement("div");
    Object.assign(row.style, {
      position: "relative", display: "flex", alignItems: "center",
      height: `${rowH}px`, whiteSpace: "nowrap",
    });
    const slot = document.createElement("div");
    Object.assign(slot.style, {
      position: "relative", width: `${iconCol}px`, height: `${rowH}px`,
      flex: "0 0 auto", marginRight: `${gap}px`,
    });
    const dot = document.createElement("div");
    Object.assign(dot.style, {
      position: "absolute",
      left: `${(iconCol - dotSize) / 2}px`, top: `${(rowH - dotSize) / 2}px`,
      width: `${dotSize}px`, height: `${dotSize}px`, borderRadius: "50%",
      border: `1.5px solid ${cfg.checklistMutedColor}`, boxSizing: "border-box",
      opacity: "1", transition: "opacity 200ms ease",
    });
    const tick = makeChecklistTick(cfg, iconCol, rowH);
    slot.append(dot, tick);
    const text = document.createElement("span");
    text.textContent = label;
    Object.assign(text.style, {
      color: cfg.checklistMutedColor, opacity: "0.8", fontWeight: "500",
      textDecoration: "none",
      transition: "color 240ms ease, opacity 240ms ease",
    });
    row.append(slot, text);
    card.appendChild(row);
    return { dot, tick, text };
  });

  function markPending(i) {
    const r = rows[i];
    r.dot.style.opacity = "1";
    r.tick.style.opacity = "0"; r.tick.style.transform = "scale(0.5)";
    r.text.style.color = cfg.checklistMutedColor;
    r.text.style.opacity = "0.8"; r.text.style.fontWeight = "500";
    r.text.style.textDecoration = "none";
  }
  function markActive(i) {
    const r = rows[i];
    r.dot.style.opacity = "0"; // the logo sits over the dot
    r.tick.style.opacity = "0"; r.tick.style.transform = "scale(0.5)";
    r.text.style.color = cfg.checklistTextColor;
    r.text.style.opacity = "1"; r.text.style.fontWeight = "600";
    r.text.style.textDecoration = "none";
  }
  function markDone(i) {
    const r = rows[i];
    r.dot.style.opacity = "0";
    r.tick.style.opacity = "1"; r.tick.style.transform = "scale(1)";
    r.text.style.color = cfg.checklistMutedColor;
    r.text.style.opacity = "0.6"; r.text.style.fontWeight = "500";
    r.text.style.textDecoration = "line-through";
  }
  function moveLogo(i) {
    logoWrap.style.opacity = "1";
    logoWrap.style.transform = `translateY(${i * rowH}px)`;
  }

  let destroyed = false;
  return {
    el: card,
    advanceTo(idx) {
      if (destroyed) return;
      for (let i = 0; i < rows.length; i++) {
        if (i < idx) markDone(i);
        else if (i === idx) markActive(i);
        else markPending(i);
      }
      moveLogo(idx);
    },
    complete() {
      if (destroyed) return;
      for (let i = 0; i < rows.length; i++) markDone(i);
      logoWrap.style.opacity = "0"; // retire the logo once everything's ticked
      try { pulse.cancel(); } catch {}
    },
    fadeIn() { if (!destroyed) { card.style.opacity = "1"; card.style.transform = "translateY(0)"; } },
    fadeOut() { if (!destroyed) { card.style.opacity = "0"; card.style.transform = "translateY(-5px)"; } },
    destroy() {
      destroyed = true;
      try { pulse.cancel(); } catch {}
      card.remove();
    },
  };
}
